import React, { createContext, useRef } from 'react';
import axios, { AxiosInstance } from 'axios';
import qs from 'qs';
import { useUser } from './user.context';
import { getCookie } from '../utils';
import { plainToInstance } from 'class-transformer';
import { UserDetails } from '../types/schemas/abc';
import { endpoints } from '../constants/endpoints';

export const CSRF_ACCESS_COOKIE = 'csrf_access_token';

// We provide the base axios instance as a default.
export const AxiosContext = createContext<AxiosInstance>(axios);

export const AxiosInstanceProvider = ({
  config = {},
  children,
}: {
  config?: any;
  children: React.ReactNode;
}) => {
  // Use a ref to ensure the axios instance is created only once per component lifecycle.
  const instanceRef = useRef<AxiosInstance | null>(null);
  const { storeUserDetails, clearUserDetails } = useUser();

  if (instanceRef.current === null) {
    const axiosInstance = axios.create(config);

    axiosInstance.interceptors.request.use(req => {
      // VITAL: This tells axios to send cookies with cross-origin requests.
      // The proxy makes this "same-origin" from the browser's perspective.
      req.withCredentials = true; 
      req.paramsSerializer = p => qs.stringify(p, { indices: false });
    
      const token = localStorage.getItem('accessToken');
      if (token) {
        req.headers.Authorization = `Bearer ${token}`;
      }
    
      const csrf = getCookie(CSRF_ACCESS_COOKIE);
      if (csrf) {
        req.headers['X-CSRF-TOKEN'] = csrf;
      }
    
      return req;
    });
    
    // Response Interceptor: Handles expired access tokens by attempting to refresh them.
    axiosInstance.interceptors.response.use(
      // For successful responses, just return the response.
      res => res,
      // For error responses, check if it's an authentication error (401).
      async err => {
        const originalRequest = err.config;

        // Check if it's a 401 error and we haven't already tried to refresh.
        if (err.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true; // Mark that we are retrying this request.
          
          try {
            // THE CORE FIX: The refresh logic is self-contained here.
            // We use the base 'axios' instance to prevent an infinite loop of interceptors.
            // The 'withCredentials: true' ensures the refreshToken cookie is sent.
            const { data } = await axios.post(endpoints.refresh, undefined, {
              withCredentials: true,
            });
            
            // If refresh is successful, update user state and localStorage with the new token.
            storeUserDetails(plainToInstance(UserDetails, data.user), data.accessToken);
            localStorage.setItem('accessToken', data.accessToken);
            
            // Update the header of the original failed request with the new token.
            originalRequest.headers['Authorization'] = `Bearer ${data.accessToken}`;
            
            // Retry the original request with the new token.
            return axiosInstance(originalRequest);

          } catch (refreshError) {
            // If the refresh attempt fails, the user's session is truly invalid.
            clearUserDetails();
            localStorage.removeItem('accessToken');
            
            // Redirect to the login page to re-authenticate.
            window.location.href = '/login'; 
            
            return Promise.reject(refreshError);
          }
        }
    
        // For any other error, just reject the promise.
        return Promise.reject(err);
      }
    );
    
    instanceRef.current = axiosInstance;
  }

  return <AxiosContext.Provider value={instanceRef.current}>{children}</AxiosContext.Provider>;
};